// pages/TicketManagement/components/TicketViewModal.jsx
import React, { useState, useEffect } from "react";
import { useSession } from "hooks/useSession";
import { useRolePermissions } from "./useRolePermissions";
import { TicketManagement } from "./ticketManagement";
import { fetchWrapper } from "utils/apiFetch";

const S = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 1060,
    background: "rgba(15, 23, 42, 0.5)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "16px", animation: "fadeIn .18s ease",
  },
  modal: {
    background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "16px",
    width: "100%", maxWidth: "820px", maxHeight: "90vh",
    display: "flex", flexDirection: "column",
    boxShadow: "0 20px 30px -10px rgba(0, 0, 0, 0.15), 0 10px 15px -5px rgba(0, 0, 0, 0.08)",
    animation: "slideUp .22s cubic-bezier(.34,1.56,.64,1)",
    overflow: "hidden",
  },
  header: {
    padding: "16px 20px 14px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc",
    display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexShrink: 0,
  },
  body: { display: "flex", flexWrap: "wrap", flex: 1, overflowY: "auto", maxHeight: "calc(90vh - 70px)" },
  left: { flex: "1 1 360px", overflowY: "auto", padding: "18px 20px", borderRight: "1px solid #e2e8f0", background: "#ffffff", minWidth: "280px" },
  right: { flex: "1 1 260px", overflowY: "auto", padding: "18px 18px", background: "#f8fafc", minWidth: "240px" },
  badge: (color, bg, border) => ({
    display: "inline-flex", alignItems: "center", gap: "5px",
    background: bg, color, border: border ? `1px solid ${border}` : "none",
    fontSize: "11px", fontWeight: 700,
    padding: "3px 10px", borderRadius: "20px", whiteSpace: "nowrap",
  }),
  metaCard: {
    background: "#ffffff", border: "1px solid #e2e8f0",
    borderRadius: "10px", padding: "12px 14px", marginBottom: "10px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
  },
  metaLabel: {
    fontSize: "10px", fontWeight: 700, letterSpacing: "0.8px",
    textTransform: "uppercase", color: "#64748b", marginBottom: "4px",
  },
  metaValue: { fontSize: "13.5px", fontWeight: 600, color: "#0f172a" },
  mono: { fontFamily: "'IBM Plex Mono', monospace", fontSize: "12.5px", color: "#0d9488", fontWeight: 700 },
  sectionHead: {
    fontSize: "11px", fontWeight: 700, letterSpacing: "0.8px",
    textTransform: "uppercase", color: "#475569",
    marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px",
  },
  commentItem: (internal) => ({
    background: internal ? "#fffbeb" : "#f8fafc",
    border: `1px solid ${internal ? "#fde68a" : "#e2e8f0"}`,
    borderLeft: internal ? "3px solid #f59e0b" : "1px solid #e2e8f0",
    borderRadius: "8px", padding: "12px 14px", marginBottom: "10px",
  }),
  commentInput: {
    width: "100%", background: "#ffffff", border: "1px solid #cbd5e1",
    borderRadius: "8px", color: "#0f172a", fontSize: "13px",
    padding: "9px 12px", resize: "vertical", minHeight: "70px",
    outline: "none", fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box",
  },
  statusBtn: (active) => ({
    width: "100%", padding: "8px 12px", borderRadius: "8px",
    border: active ? "1px solid #0d9488" : "1px solid #e2e8f0",
    background: active ? "#f0fdfa" : "#ffffff",
    color: active ? "#0d9488" : "#475569",
    fontSize: "12.5px", fontWeight: 600, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: "6px", transition: "all .15s",
    fontFamily: "'DM Sans', sans-serif",
  }),
  closeBtn: {
    background: "#f1f5f9", border: "1px solid #e2e8f0", cursor: "pointer",
    color: "#64748b", fontSize: "16px", lineHeight: 1,
    padding: "6px 10px", borderRadius: "8px",
  },
  submitCommentBtn: {
    background: "#0d9488", border: "1px solid #0d9488", borderRadius: "8px",
    color: "#ffffff", padding: "8px 18px", fontSize: "13px", fontWeight: 700,
    cursor: "pointer", marginTop: "10px", display: "flex", alignItems: "center", gap: "6px",
    fontFamily: "'DM Sans', sans-serif",
  },
};

const BadgePriority = ({ val }) => {
  const m = TicketManagement.badgeMap.priority[val] || { color: "#475569", bg: "#f1f5f9", border: "#cbd5e1", label: val };
  return <span style={S.badge(m.color, m.bg, m.border)}>{m.label}</span>;
};
const BadgeStatus = ({ val }) => {
  const m = TicketManagement.badgeMap.status[val] || { color: "#475569", bg: "#f1f5f9", border: "#cbd5e1", label: val };
  return <span style={S.badge(m.color, m.bg, m.border)}>{m.label}</span>;
};

/* ═══════════════════════════════════════════
   MAIN
═══════════════════════════════════════════ */
const TicketViewModal = ({ show, onClose, ticket, onStatusChange, onEditOpen }) => {
  const { userid } = useSession();
  const { can, roleName, isAdmin } = useRolePermissions(TicketManagement);
  const [comments, setComments]   = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [posting, setPosting]     = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    if (!show || !ticket?.id) return;
    setComments([]);
    setNewComment("");
    const fd = new FormData();
    fd.append("action", "GET_COMMENTS");
    fd.append("ticket_id", ticket.id);
    fd.append("userid", userid);
    fetchWrapper(TicketManagement.apiUrl, { method: "POST", body: fd })
      .then(r => setComments(r?.data || []))
     
      .catch(() => {});
  }, [show, ticket?.id, userid]);

  const handlePostComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    const fd = new FormData();
    fd.append("action", "ADD_COMMENT");
    fd.append("ticket_id", ticket.id);
    fd.append("userid", userid);
    fd.append("content", newComment);
    fd.append("is_internal", isInternal ? "1" : "0");
    try {
      const d = await fetchWrapper(TicketManagement.apiUrl, { method: "POST", body: fd });
     
      if (d?.status === "success" || d?.success) {
        setComments(prev => [...prev, d.comment || {
          id: Date.now(), content: newComment, is_internal: isInternal,
          author_name: "You", created_at: new Date().toISOString(),
        }]);
        setNewComment("");
      }
    } catch {}
    setPosting(false);
  };

  const handleQuickStatus = async (newStatus) => {
    if (newStatus === ticket?.status || statusLoading) return;
    setStatusLoading(true);
    const fd = new FormData();
    const ticketId = ticket.id || ticket.ticket_id;
    fd.append("ticket_id", ticketId);
    fd.append("id", ticketId);
    fd.append("status", newStatus);
    fd.append("userid", userid);
    try {
      const d = await fetchWrapper(TicketManagement.apiUrl, { method: "POST", body: fd });
      if (d?.status === "success" || d?.success || d?.status === true) onStatusChange?.(newStatus);
    } catch {}
    setStatusLoading(false);
  };

  if (!show || !ticket) return null;

  const tagList = (ticket.tags || "").split(",").map(t => t.trim()).filter(Boolean);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes slideUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .tv-status-btn:hover { border-color:#0d9488!important; color:#0f172a!important; background:#f0fdfa!important; }
        .tv-close:hover { color:#0f172a!important; background:#e2e8f0!important; }
        .tv-edit-btn:hover { background:#f1f5f9!important; color:#0f172a!important; }
      `}</style>

      <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={S.modal}>

          {/* ── Header ── */}
          <div style={S.header}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "8px" }}>
                <span style={{ ...S.mono, fontSize: "14px" }}>{ticket.ticket_id || `#${ticket.id}`}</span>
                <BadgePriority val={ticket.priority} />
                <BadgeStatus val={ticket.status} />
                <span style={{ ...S.badge("#6366f1", "#eef2ff", "#c7d2fe") }}>
                  {TicketManagement.ticketTypeOptions.find(o => o.value === ticket.ticket_type)?.label || ticket.ticket_type}
                </span>
              </div>
              <h2 style={{ fontSize: "17px", fontWeight: 700, color: "#0f172a", margin: 0, fontFamily: "'DM Sans', sans-serif" }}>
                {ticket.title}
              </h2>
            </div>
            <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
              {can.edit && (
                <button onClick={onEditOpen} className="tv-edit-btn" style={{
                  background: "#ffffff", border: "1px solid #cbd5e1",
                  borderRadius: "8px", color: "#334155", padding: "7px 14px",
                  fontSize: "12.5px", fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                  transition: "all .15s",
                }}>
                  ✏️ Edit
                </button>
              )}
              <button style={S.closeBtn} className="tv-close" onClick={onClose}>✕</button>
            </div>
          </div>

          {/* ── Body ── */}
          <div style={S.body}>

            {/* ── LEFT: Description + Comments ── */}
            <div style={S.left}>

              {/* Description */}
              <div style={{ marginBottom: "20px" }}>
                <div style={S.sectionHead}>📄 Description</div>
                <div style={{
                  fontSize: "14px", color: "#334155", lineHeight: "1.75",
                  background: "#f8fafc", border: "1px solid #e2e8f0",
                  borderRadius: "10px", padding: "14px 16px",
                }}>
                  {ticket.description || <em style={{ color: "#94a3b8" }}>No description provided.</em>}
                </div>
              </div>

              {/* Financial Info */}
              {(ticket.amount_involved || ticket.reference_number) && (
                <div style={{ marginBottom: "20px" }}>
                  <div style={S.sectionHead}>💰 Financial Details</div>
                  <div style={{
                    background: "#f0fdfa", border: "1px solid #ccfbf1",
                    borderRadius: "10px", padding: "14px 16px",
                    display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px",
                  }}>
                    {ticket.amount_involved && (
                      <div>
                        <div style={S.metaLabel}>Amount</div>
                        <div style={{ fontSize: "20px", fontWeight: 700, color: "#0d9488", fontFamily: "'IBM Plex Mono', monospace" }}>
                          {ticket.currency || "INR"} {Number(ticket.amount_involved).toLocaleString("en-IN")}
                        </div>
                      </div>
                    )}
                    {ticket.reference_number && (
                      <div>
                        <div style={S.metaLabel}>Reference / TXN</div>
                        <div style={{ ...S.mono, fontSize: "13.5px", color: "#0f172a" }}>{ticket.reference_number}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tags */}
              {tagList.length > 0 && (
                <div style={{ marginBottom: "20px" }}>
                  <div style={S.sectionHead}>🏷 Tags</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {tagList.map((tag, i) => (
                      <span key={i} style={{
                        background: "#f1f5f9", border: "1px solid #cbd5e1",
                        color: "#475569", fontSize: "11px", fontWeight: 600, padding: "3px 10px", borderRadius: "20px",
                      }}>
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Comments Thread */}
              <div>
                <div style={S.sectionHead}>
                  💬 Comments
                  <span style={{ background: "#e2e8f0", color: "#475569", fontSize: "10px", fontWeight: 700, padding: "2px 7px", borderRadius: "10px" }}>
                    {comments.length}
                  </span>
                </div>

                {comments.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "24px", color: "#94a3b8", fontSize: "13px" }}>
                    <div style={{ fontSize: "28px", marginBottom: "8px", opacity: .5 }}>💬</div>
                    No comments yet.
                  </div>
                ) : (
                  comments.map((c, i) => (
                    <div key={c.id || i} style={S.commentItem(c.is_internal)}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "7px" }}>
                        <div style={{
                          width: "22px", height: "22px", borderRadius: "50%",
                          background: "#ccfbf1", color: "#0d9488",
                          fontSize: "10px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          {(c.author_name || "?")[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize: "13px", fontWeight: 600, color: "#0f172a" }}>{c.author_name}</span>
                        <span style={{ fontSize: "11px", color: "#64748b", fontFamily: "'IBM Plex Mono', monospace" }}>
                          {c.created_at ? new Date(c.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""}
                        </span>
                        {c.is_internal && (
                          <span style={{ fontSize: "10px", fontWeight: 700, color: "#b45309", background: "#fef3c7", border: "1px solid #fde68a", padding: "1px 6px", borderRadius: "4px" }}>
                            INTERNAL
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "13.5px", color: "#334155", lineHeight: "1.6" }}>
                        {c.content}
                      </div>
                    </div>
                  ))
                )}

                {/* Add Comment */}
                <div style={{ marginTop: "16px", paddingTop: "14px", borderTop: "1px solid #e2e8f0" }}>
                  <textarea
                    value={newComment} onChange={e => setNewComment(e.target.value)}
                    placeholder="Add a comment or update..."
                    style={S.commentInput} className="tm-input"
                  />
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "8px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "7px", cursor: "pointer", fontSize: "12.5px", color: "#475569" }}>
                      <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)}
                        style={{ accentColor: "#0d9488" }} />
                      Internal note (not visible to customer)
                    </label>
                    <button style={S.submitCommentBtn} onClick={handlePostComment} disabled={posting || !newComment.trim()}>
                      {posting ? "Posting..." : "➤ Post"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ── RIGHT: Meta Panel ── */}
            <div style={S.right}>

              {/* Meta */}
              <div style={S.sectionHead}>📋 Details</div>

              {[
                { label: "Ticket ID",   value: ticket.ticket_id || `#${ticket.id}`, mono: true },
                { label: "Created By",  value: ticket.created_by_name || "—" },
                { label: "Assigned To", value: ticket.assigned_to_name || "Unassigned" },
                { label: "Category",    value: ticket.category || "—" },
                { label: "Created",     value: ticket.created_at ? new Date(ticket.created_at).toLocaleDateString("en-IN") : "—" },
                { label: "Updated",     value: ticket.updated_at ? new Date(ticket.updated_at).toLocaleDateString("en-IN") : "—" },
                ...(ticket.due_date ? [{ label: "Due Date", value: new Date(ticket.due_date).toLocaleString("en-IN"), warn: true }] : []),
                ...(ticket.resolved_at ? [{ label: "Resolved At", value: new Date(ticket.resolved_at).toLocaleString("en-IN"), success: true }] : []),
              ].map(({ label, value, mono, warn, success }) => (
                <div key={label} style={S.metaCard}>
                  <div style={S.metaLabel}>{label}</div>
                  <div style={{
                    ...S.metaValue,
                    ...(mono ? S.mono : {}),
                    ...(warn ? { color: "#d97706" } : {}),
                    ...(success ? { color: "#16a34a" } : {}),
                  }}>{value}</div>
                </div>
              ))}

              {/* Quick Status — admins only */}
              {(isAdmin || can.edit) && (
                <>
                  <div style={{ ...S.sectionHead, marginTop: "16px" }}>⚡ Quick Status</div>
                  {TicketManagement.statusOptions.map(opt => {
                    const isActive = ticket.status === opt.value;
                    const m = TicketManagement.badgeMap.status[opt.value];
                    return (
                      <button
                        key={opt.value}
                        style={S.statusBtn(isActive)}
                        className="tv-status-btn"
                        onClick={() => handleQuickStatus(opt.value)}
                        disabled={statusLoading}
                      >
                        <span style={isActive ? S.badge(m?.color, m?.bg, m?.border) : { color: "#475569" }}>
                          {opt.label}
                        </span>
                        {isActive && <span style={{ color: "#0d9488", fontSize: "13px" }}>✓</span>}
                      </button>
                    );
                  })}
                </>
              )}

              {/* Role indicator */}
              <div style={{
                marginTop: "16px", padding: "10px 12px",
                background: "#f0fdfa", border: "1px solid #ccfbf1",
                borderRadius: "8px", fontSize: "11px", color: "#0f766e",
              }}>
                Viewing as <strong style={{ color: "#0d9488" }}>{roleName.replace(/_/g, " ").toUpperCase()}</strong>
                {!can.edit && <div style={{ marginTop: "4px", color: "#b45309" }}>⚠ View-only access</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketViewModal;